import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './ActivityLog.css';

const ActivityLog = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0
  });
  const [natureOptions, setNatureOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedNature, setSelectedNature] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    fetchActivities(pagination.currentPage);
  }, []);

  const fetchActivities = async (pageNumber = 1, searchTerm = undefined, nature = undefined, propertyId = undefined, status = undefined) => {
    try {
      setLoading(true);
      let queryString = `pageNumber=${pageNumber}`;
      
      // Handle search criteria
      const searchValue = searchTerm !== undefined ? searchTerm : searchCriteria;
      if (searchValue) {
        queryString += `&searchCriteria=${encodeURIComponent(searchValue)}`;
      }
      
      // Use provided values or fall back to state values
      // Normalize empty strings to null so they don't get sent to the endpoint
      const natureValue = nature !== undefined 
        ? (nature === "" ? null : nature)
        : (selectedNature === "" ? null : selectedNature || null);
      
      const propertyValue = propertyId !== undefined
        ? (propertyId === "" || propertyId === null ? null : propertyId)
        : (selectedProperty === "" ? null : (selectedProperty ? parseInt(selectedProperty) : null));
      
      const statusValue = status !== undefined
        ? (status === "" ? null : status)
        : (selectedStatus === "" ? null : selectedStatus || null);
      
      // Add filter parameters to query string only if they have values
      if (natureValue) {
        queryString += `&nature=${encodeURIComponent(natureValue)}`;
      }
      if (propertyValue) {
        queryString += `&propertyId=${propertyValue}`;
      }
      if (statusValue) {
        queryString += `&status=${encodeURIComponent(statusValue)}`;
      }
      
      const data = await apiService.get(`/activity?${queryString}`);
      
      // Handle new response structure with pagination and filter options
      if (data.activities && data.pagination) {
        setActivities(data.activities);
        setPagination(data.pagination);
        
        // Update filter options if they exist in response (using lowercase property names)
        if (data.nature_options) {
          setNatureOptions(data.nature_options);
        }
        if (data.property_options) {
          setPropertyOptions(data.property_options);
        }
        if (data.status_options) {
          setStatusOptions(data.status_options);
        }
      } else if (data.pagination) {
        // Handle case where activities might be at root level
        setActivities(Array.isArray(data) ? data : (data.activities || []));
        setPagination(data.pagination);
        
        // Update filter options if they exist in response (using lowercase property names)
        if (data.nature_options) {
          setNatureOptions(data.nature_options);
        }
        if (data.property_options) {
          setPropertyOptions(data.property_options);
        }
        if (data.status_options) {
          setStatusOptions(data.status_options);
        }
      } else {
        // Fallback for backward compatibility
        setActivities(Array.isArray(data) ? data : []);
        
        // Still try to get filter options even if pagination structure is different
        if (data.nature_options) {
          setNatureOptions(data.nature_options);
        }
        if (data.property_options) {
          setPropertyOptions(data.property_options);
        }
        if (data.status_options) {
          setStatusOptions(data.status_options);
        }
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching activities:", err);
      setError("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSearch = () => {
    // Reset to page 1 when filtering
    fetchActivities(1);
  };

  const handleClear = async () => {
    // Clear all state values first
    setSearchCriteria("");
    setSelectedNature("");
    setSelectedProperty("");
    setSelectedStatus("");
    
    // Call endpoint with only pageNumber
    try {
      setLoading(true);
      const data = await apiService.get('/activity?pageNumber=1');
      
      // Handle new response structure with pagination
      if (data.activities && data.pagination) {
        setActivities(data.activities);
        setPagination(data.pagination);
        
        // Update filter options if they exist in response
        if (data.nature_options) {
          setNatureOptions(data.nature_options);
        }
        if (data.property_options) {
          setPropertyOptions(data.property_options);
        }
        if (data.status_options) {
          setStatusOptions(data.status_options);
        }
      } else if (data.pagination) {
        setActivities(Array.isArray(data) ? data : (data.activities || []));
        setPagination(data.pagination);
        
        if (data.nature_options) {
          setNatureOptions(data.nature_options);
        }
        if (data.property_options) {
          setPropertyOptions(data.property_options);
        }
        if (data.status_options) {
          setStatusOptions(data.status_options);
        }
      } else {
        setActivities(Array.isArray(data) ? data : []);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching activities:", err);
      setError("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  const handleNatureFilterChange = (e) => {
    const value = e.target.value;
    setSelectedNature(value);
    // Reset to page 1 when filter changes
    fetchActivities(1, undefined, value === "" ? null : value, undefined, undefined);
  };

  const handlePropertyFilterChange = (e) => {
    const value = e.target.value;
    setSelectedProperty(value);
    // Reset to page 1 when filter changes
    fetchActivities(1, undefined, undefined, value === "" ? null : (value ? parseInt(value) : null), undefined);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setSelectedStatus(value);
    // Reset to page 1 when filter changes
    fetchActivities(1, undefined, undefined, undefined, value === "" ? null : value);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchActivities(newPage);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "information":
        return "information";
      case "wip":
        return "wip";
      default:
        return "default";
    }
  };

  const getNatureColor = (nature) => {
    switch (nature.toLowerCase()) {
      case "property details viewed":
        return "viewed";
      case "property enquiry":
        return "enquiry";
      case "investment enquiry":
        return "investment";
      case "self registration":
        return "registration";
      default:
        return "default";
    }
  };

  const getDisplayNames = (addedByName, userName, cbrexPersonalise) => {
    // If either name is empty, replace both with cbrexPersonalise last 12 digits
    if ((!addedByName || addedByName.trim() === '') || (!userName || userName.trim() === '')) {
      const fallbackValue = cbrexPersonalise ? `[${cbrexPersonalise.slice(-12)}]` : '-';
      return {
        addedBy: fallbackValue,
        user: fallbackValue,
        isFallback: true
      };
    }
    
    // Both names are present, use them as is
    return {
      addedBy: addedByName,
      user: userName,
      isFallback: false
    };
  };

  const handleRowClick = (e, activity) => {
    // Prevent navigation if clicking on a select dropdown or button
    if (e.target.tagName === 'SELECT' || e.target.tagName === 'BUTTON' || e.target.closest('select') || e.target.closest('button')) {
      return;
    }
    
    // Try different possible ID field names
    const activityId = activity.id || activity.activityId || activity.activity_id || activity.ID;
    
    if (activityId) {
      navigate(`/activity-log/${activityId}`);
    } else {
      console.error('Activity object does not have an ID field. Available fields:', Object.keys(activity));
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Activity Log</h1>
        </div>
        <div className="loading-state">
          <p>Loading activity log...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Activity Log</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => fetchActivities(pagination.currentPage)} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Activity Log</h1>
        {pagination.totalCount > 0 && (
          <div className="pagination-info">
            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} activities
          </div>
        )}
      </div>

      {/* Filter and Search Controls */}
      <div className="filter-controls">
        <div className="filter-left">
          <label htmlFor="keywordSearch">Keyword search:</label>
          <input
            type="text"
            id="keywordSearch"
            className="keyword-input"
            value={searchCriteria}
            onChange={(e) => setSearchCriteria(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleFilterSearch();
              }
            }}
            placeholder="Enter search term"
          />
          <button
            type="button"
            onClick={handleFilterSearch}
            className="filter-search-btn"
          >
            Filter / search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="clear-btn"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="properties-table">
        <table>
          <thead>
            {/* Filter dropdowns row */}
            <tr className="filter-row">
              <th></th>
              <th>
                <select
                  id="natureFilter"
                  className="column-filter-select"
                  value={selectedNature}
                  onChange={handleNatureFilterChange}
                >
                  <option value="">All Natures</option>
                  {natureOptions.map((option, index) => (
                    <option key={index} value={option.nature}>
                      {option.nature}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  id="statusFilter"
                  className="column-filter-select"
                  value={selectedStatus}
                  onChange={handleStatusFilterChange}
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map((option, index) => (
                    <option key={index} value={option.statusDescription}>
                      {option.statusDescription}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  id="propertyFilter"
                  className="column-filter-select"
                  value={selectedProperty}
                  onChange={handlePropertyFilterChange}
                >
                  <option value="">All Properties</option>
                  {propertyOptions.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.listingTitle}
                    </option>
                  ))}
                </select>
              </th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
            {/* Column headers row */}
            <tr>
              <th>Date</th>
              <th>Nature</th>
              <th>Status</th>
              <th>Property</th>
              <th>Added By</th>
              <th>User</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((activity, index) => {
              const displayNames = getDisplayNames(
                activity.addedByName, 
                activity.userName, 
                activity.cbrexPersonalise
              );
              
              return (
                <tr 
                  key={index}
                  onClick={(e) => handleRowClick(e, activity)}
                  className="clickable-row"
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{activity.dateOnly}</strong>
                  </td>
                  <td>
                    {activity.nature}
                  </td>
                  <td>
                    {activity.statusDescription}
                  </td>
                  <td>
                    <div className="property-info">
                      {activity.propertyTitle ? (
                        <strong>{activity.propertyTitle}</strong>
                      ) : (
                        <span className="no-property">-</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="user-info">
                      <span 
                        className={displayNames.isFallback ? 'fallback-text' : ''}
                        style={displayNames.isFallback ? { fontStyle: 'italic', fontWeight: '300', color: 'rgba(255, 255, 255, 0.6)' } : {}}
                      >
                        {displayNames.addedBy}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="user-info">
                      <span 
                        className={displayNames.isFallback ? 'fallback-text' : ''}
                        style={displayNames.isFallback ? { fontStyle: 'italic', fontWeight: '300', color: 'rgba(255, 255, 255, 0.6)' } : {}}
                      >
                        {displayNames.user}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="comments">
                      {activity.comments}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-pages">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => {
              // Show first page, last page, current page, and pages around current
              const showPage = 
                pageNum === 1 ||
                pageNum === pagination.totalPages ||
                (pageNum >= pagination.currentPage - 2 && pageNum <= pagination.currentPage + 2);
              
              if (!showPage) {
                // Show ellipsis
                if (pageNum === pagination.currentPage - 3 || pageNum === pagination.currentPage + 3) {
                  return <span key={pageNum} className="pagination-ellipsis">...</span>;
                }
                return null;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-btn ${pageNum === pagination.currentPage ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
